import { apiClient } from "@/lib/api/client";
import { API_ENDPOINTS } from "@/lib/api/endpoints";
import type {
  FormListItem,
  FormCreateRequest,
  FormUpdateRequest,
  FormResponse,
} from "../types/forms.types";

export interface ListFormsParams {
  /** Exact tax year (required unless beforeTaxYear is used). */
  taxYear?: number;
  /** List forms with taxYear strictly less than this value. */
  beforeTaxYear?: number;
  filingType?: "INDIVIDUAL" | "BUSINESS";
}

/** Normalize a list item so formName is set from formName or form_name (API may use either). */
function normalizeFormListItem(
  item: FormListItem & { form_name?: string }
): FormListItem {
  const formName = item.formName ?? item.form_name;
  const { form_name: _omit, ...rest } = item;
  return { ...rest, formName };
}

/** List form instances (GET /api/forms). Provide taxYear or beforeTaxYear. */
export async function listForms(
  params?: ListFormsParams
): Promise<FormListItem[]> {
  const response = await apiClient.get<(FormListItem & { form_name?: string })[]>(
    API_ENDPOINTS.FORMS,
    { params: params ?? undefined }
  );
  const raw = Array.isArray(response.data) ? response.data : [];
  return raw.map(normalizeFormListItem);
}

/** Create a form instance (POST /api/forms). Returns created form with id. */
export async function createForm(
  body: FormCreateRequest
): Promise<FormResponse> {
  const response = await apiClient.post<FormResponse>(
    API_ENDPOINTS.FORMS,
    body
  );
  return response.data;
}

/** Get a form by id (GET /api/forms/{id}). */
export async function getFormById(id: string): Promise<FormResponse> {
  const response = await apiClient.get<FormResponse>(
    API_ENDPOINTS.FORM_BY_ID(id)
  );
  return response.data;
}

/** Update a form (PUT /api/forms/{id}). Send only fields to update. */
export async function updateForm(
  id: string,
  body: FormUpdateRequest
): Promise<FormResponse> {
  const response = await apiClient.put<FormResponse>(
    API_ENDPOINTS.FORM_BY_ID(id),
    body
  );
  return response.data;
}

/** Delete a form (DELETE /api/forms/{id}). Returns 204 on success. */
export async function deleteForm(id: string): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.FORM_BY_ID(id));
}
